import { useMemo } from 'react';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Typography } from '@mui/material';
import _, { isArray } from 'lodash';
import clsx from 'clsx';

const DEFAULT_ITEMS_COLLECTION = [25, 50, 100, 200];

/**
 * Paginator
 *
 * Componente de paginación con selección de items por página y navegación de páginas.
 *
 * @component
 * @param {object} [props] - Props adicionales para el contenedor
 * @param {function} [onChangeCurrentPage] - Callback al cambiar de página
 * @param {function} [onChangeItemsPerPage] - Callback al cambiar items por página
 * @param {number} [totalPages=0] - Total de páginas
 * @param {number} [totalItems=0] - Total de items
 * @param {number} [currentItemsPerPage=25] - Items por página seleccionados
 * @param {number} [currentPage=1] - Página actual
 * @param {boolean} [isCompacted=false] - Componente compacto
 * @param {boolean} [showBoundaryButtons=false] - Muestra botones para ultima o primera pagina
 * @param {Array} [itemsCollection] - Sobrescribe la lista por default de elementos por paginación
 * @param {object} [style={}] - Estilos adicionales para el contenedor
 * @returns {JSX.Element}
 */
export const Paginator = ({
  props,
  onChangeCurrentPage = () => { },
  onChangeItemsPerPage = () => { },
  totalPages = 0,
  totalItems = 0,
  currentItemsPerPage = 25,
  currentPage = 1,
  isCompacted = false,
  style = {},
  itemsCollection,
  showBoundaryButtons = false,
}) => {

  const items = useMemo(() => {
    const currItems = Array.isArray(itemsCollection)
      ? itemsCollection
      : DEFAULT_ITEMS_COLLECTION;

    return currItems.filter((item) => _.isString(item) || _.isNumber(item));
  }, [itemsCollection]);

  const handleChangeItems = ({ target }) => {
    onChangeItemsPerPage(target.value);
  };

  const handleChangePage = (event, number) => {
    onChangeCurrentPage(number);
  };

  return (
    <div
      {...props}
      className="flex justify-center items-center"
      style={{ maxWidth: '49rem', ...style }}
    >
      <div className="py-4 px-20 flex flex-row justify-center items-center gap-6 flex-wrap">
        <div className="flex flex-row justify-center sm:justify-end items-center pr-16">
          <div className="mr-16" style={{ minWidth: '50px' }}>
            <Typography className="text-base p-0 m-0" gutterBottom>
              {`Total ${totalItems}`}
            </Typography>
          </div>
          {items.length > 0 && (
            <FormControl>
              <InputLabel id="select-pagination">Items/Pag</InputLabel>
              <Select
                variant="outlined"
                labelId="select-pagination"
                id="select-pagination"
                value={items.includes(currentItemsPerPage) ? currentItemsPerPage : items[0]}
                onChange={handleChangeItems}
                input={<OutlinedInput id="select-pagination-label" label="No Items" />}
                label="Items"
                size="small"
                sx={{ justifyContent: 'center', alignItems: 'center', minWidth: '8rem' }}
              >
                {items.map((item, index) => (
                  <MenuItem key={`${index}-${String(item)}`} value={item}>{String(item)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <Pagination
          size="small"
          page={currentPage}
          count={totalPages}
          onChange={handleChangePage}
          boundaryCount={isCompacted ? 0 : 1}
          siblingCount={isCompacted ? 0 : 0}
          showFirstButton={showBoundaryButtons}
          showLastButton={showBoundaryButtons}
          className={clsx('py-8', isCompacted ? 'min-w-[14rem]' : 'min-w-[21rem]')}
          sx={
            isCompacted
              ? {
                '& .MuiPaginationItem-root': {
                  minWidth: '24px',
                  height: '24px',
                  margin: '0 2px',
                  padding: '0',
                },
              }
              : {}
          }
        />
      </div>
    </div>
  );
};
